import React from "react";
import {
  Button,
  CircularProgress,
  Paper,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export type MediaKitSaveBarProps = {
  visible: boolean;
  saving: boolean;
  canSave: boolean;
  /** Shown instead of the default "unsaved changes" text (validation or save error). */
  message?: string | null;
  messageSeverity?: "warning" | "error";
  onDiscard: () => void;
  onSave: () => void;
};

export const MediaKitSaveBar: React.FC<MediaKitSaveBarProps> = ({
  visible,
  saving,
  canSave,
  message,
  messageSeverity = "warning",
  onDiscard,
  onSave,
}) => {
  const { t } = useTranslation("mediaKit");
  return (
  <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
    <Paper
      elevation={8}
      sx={{
        position: "sticky",
        bottom: 16,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        px: { xs: 2, sm: 3 },
        py: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Typography
          variant="body2"
          fontWeight={500}
          color={message ? `${messageSeverity}.main` : "text.primary"}
        >
          {message || t("unsaved")}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onDiscard} disabled={saving} color="inherit">
            {t("discard")}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving || !canSave}
            disableElevation
            sx={{ minWidth: 150 }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("saveChanges")
            )}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  </Slide>
  );
};
